import { useMemo, useState } from 'react'
import {
  DataSurface,
  GamesPanel,
  StatCard,
  StatGrid,
  TableCard,
  Td,
  Thead,
  Tr,
  ChildrenIcon,
  PlayIcon,
  SchoolIcon,
  PointIcon,
  ReportsIcon,
} from '../../../shared/components'
import { useResource } from '../../../shared/hooks/useResource'
import {
  getSchoolCoverage,
  getSchoolOverview,
  type NcfCoverage,
  type SchoolOverview,
} from '../../../shared/api/school'
import { getSchoolGames, type SchoolGames } from '../../../shared/api/games'
import { GAMES_PANEL_COPY } from '../../../shared/ui/gamesCopy'
import { GAME_TITLES } from '../../../shared/ui/gameTitles'
import type { GameId } from '../../../shared/curriculum'
import type { AgeBand } from '../../../shared/types/prisma-enums'
import { exportToCsv } from '../../../shared/lib/exportData'
import type { StaffAuth } from '../../../shared/api/teaching'
import type { Lang } from '../../../shared/i18n'
import { presetRange } from './dashboardRange'
import { ADMIN_COPY, BAND_LABEL, SKILL_LABEL, describeAdminError } from './adminCopy'
import { CoverageRing, CodePill } from './SchoolPlayfulUI'
import { ChartPanel, Columns, Donut, ShareBars, SplitBars } from './SchoolCharts'

export interface SchoolReportsProps {
  lang?: Lang
  auth?: StaffAuth
}

/* 🔴 SEVEN, PINNED. `GET /school/overview` defaults to thirty now that E1
   filters it by date; this screen has always reported a week, and its "in the
   last N days" labels are read by people comparing one report to the next. */
const REPORT_WINDOW_DAYS = 7

/**
 * What this school is actually doing — the head teacher's analytics screen.
 *
 * 🔴 IT MADE NO REQUESTS AT ALL. Seven hundred and ninety lines, not one
 * `useResource`, and the five cards across the top read **348 students**, an
 * em dash, an em dash, **785 assignments** and **32m average learning time** —
 * each of them sitting above the words *"Not measured yet"*. A number and a
 * denial of the number, in the same card. Under them sat eight tabs, a
 * `Filters` button wired to nothing and an `Export` button wired to
 * `alert('Exporting full analytics report...')`.
 *
 * 🔴 AND EVERY FIGURE IT NEEDED WAS ALREADY ON THE WIRE. `GET /school/overview`
 * has returned the enrolment, the class list, the teacher count, the week's
 * sittings and the per-skill fold since the tier was built; `GET /school/games`
 * and `GET /school/coverage` have never been called by anything at all. This is
 * the same failure the operator tier had — shipped endpoints, uncalled — and the
 * §8.4 warning every file in this folder quotes is written about exactly it.
 *
 * ⚠️ THREE OF THE FIVE ORIGINAL CARDS ARE GONE RATHER THAN WIRED, AND THAT IS
 * THE HONEST HALF OF THIS WORK:
 *
 *   · **"Average Score"** — nothing scores a child. Answer accuracy exists per
 *     game and is on the games panel below, where it is labelled as what it is.
 *     A single school-wide "score" would be a grade this product refuses to
 *     award (`design-system.md` §8).
 *   · **"Assignments"** — the product has no assignments. Nothing records one.
 *   · **"Avg. Learning Time"** — no session duration is stored anywhere. `32m`
 *     was invented, and there is no query that could replace it.
 *
 * A card that can never fill is not an empty state, it is a promise — so they
 * are removed, and what replaces them is the coverage and per-game work this
 * screen's own components were built for and never given data.
 */
export function SchoolReports({ lang = 'en', auth }: SchoolReportsProps) {
  const t = ADMIN_COPY[lang]

  /** The seven days this screen has always covered. See `overviewResource`. */
  const [reportWindow] = useState(() => presetRange(REPORT_WINDOW_DAYS))

  /*
   * ⚠️ THREE RESOURCES, NOT ONE COMBINED READ. A coverage query that 403s must
   * not blank the enrolment tiles, and the games fold is the heaviest of the
   * three — a school with a term of history must not hold up the count a head
   * teacher opens this page for. Each panel carries its own four states.
   */
  /*
   * 🔴 E5 ASKS FOR ITS WEEK EXPLICITLY NOW, AND THAT IS A CHANGE THAT KEEPS
   * THIS SCREEN THE SAME RATHER THAN ONE THAT ALTERS IT.
   *
   * `GET /school/overview` used to hard-code a seven-day window and ignore what
   * anybody asked for. It takes a range now — E1 grew a date filter over it —
   * and its default became thirty days, so a call that passes nothing would
   * quietly turn every "in the last 7 days" figure on this page into a month.
   * The labels here all read `o.windowDays` and would have stayed truthful, but
   * an inspector-facing report is not the place to silently widen the period it
   * covers.
   *
   * ⚠️ A CONSTANT RANGE, NOT ONE COMPUTED IN THE RENDER. `presetRange` reads
   * the clock, so calling it inline would produce a new object — and a new
   * resource key — on every render for the seconds either side of midnight UTC.
   * `REPORT_WINDOW` below is built once per mount.
   */
  const overviewResource = useResource<SchoolOverview>(
    (signal) => getSchoolOverview({ ...auth, signal }, reportWindow),
    ['school-overview', reportWindow.from, reportWindow.to],
  )
  const gamesResource = useResource<SchoolGames>(
    (signal) => getSchoolGames({ ...auth, signal }),
    ['school-games'],
  )
  const coverageResource = useResource<NcfCoverage>(
    (signal) => getSchoolCoverage({ ...auth, signal }),
    ['school-coverage'],
  )

  const o = overviewResource.data
  const games = gamesResource.data
  const coverage = coverageResource.data

  /*
   * 🔴 WORST FIRST, AND ARCHIVED CLASSES OUT. The question a head teacher opens
   * this list with is "which class needs me", and a league table sorted the
   * usual way puts the answer at the bottom. A class with nobody enrolled has
   * nothing to be engaged about, so it sorts last rather than reading as 0%.
   */
  const classes = useMemo(() => {
    const live = (o?.classes ?? []).filter((c) => c.archivedAt === null)
    return [...live].sort((a, b) => {
      const share = (c: (typeof live)[number]) =>
        c.childCount === 0 ? Number.POSITIVE_INFINITY : c.activeThisWeek / c.childCount
      return share(a) - share(b)
    })
  }, [o])

  /*
   * 🔴 EVERY SERIES BELOW IS A FOLD OF A PAYLOAD ALREADY ON SCREEN, which is
   * what makes the charts safe to sit beside the tables. `design-system.md` §8
   * — *"sibling panels cannot contradict"* — is not a style rule here: a bar
   * chart of engagement built from a second query would disagree with the table
   * under it the first time a child played between the two requests.
   */

  /** Children by the age band of their class — a real partition, so a donut. */
  const bands = useMemo(() => {
    const tally = new Map<AgeBand, number>()
    for (const c of o?.classes ?? []) {
      if (c.archivedAt !== null) continue
      tally.set(c.ageBand, (tally.get(c.ageBand) ?? 0) + c.childCount)
    }
    const tones = ['primary', 'teal', 'indigo'] as const
    return [...tally.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([band, value], i) => ({
        key: band,
        label: BAND_LABEL[band][lang],
        value,
        tone: tones[i % tones.length],
      }))
  }, [o, lang])

  /*
   * ⚠️ ARCHIVED CLASSES ARE OUT OF EVERY CHART, for the reason the table gives:
   * a closed class is not a quiet one, and leaving it in makes a rollout look
   * worse than it is.
   */
  const liveClasses = useMemo(
    () => (o?.classes ?? []).filter((c) => c.archivedAt === null),
    [o],
  )

  /*
   * ⚠️ THE EXPORT WRITES WHAT IS ON SCREEN, and it replaces an `alert()` that
   * claimed to be "exporting full analytics report". `schoolCoverageCsvPath`
   * exists on the API module and is deliberately not used: it is a plain link
   * and this API takes a bearer token, so the JSON we already hold is the
   * honest source. Its own note says so.
   */
  const exportCoverage = () => {
    if (coverage === null) return
    exportToCsv(
      'kalqy-school-coverage',
      coverage.rows.map((row) => ({
        Game: GAME_TITLES[row.game as GameId]?.[lang] ?? row.game,
        Category: row.category,
        Codes: row.codes.join(' '),
        Events: row.events,
        Children: row.children,
      })),
    )
  }

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-8">
      <div>
        <h1 className="font-dash text-dash-2xl font-extrabold text-ink">{t.reportsTitle}</h1>
        <p className="mt-1 font-dash text-dash-sm text-muted">{t.reportsHint}</p>
      </div>

      {/*
        🔴 COUNTS AND THEIR DENOMINATORS, NEVER A PERCENTAGE — the rule
        `SchoolOverview` states on the wire, applied to the row that reads it.
        *"12 of 40 children played this week"* is a fact a head teacher can act
        on; *"30% engagement"* is a grade whose meaning depends on what it was
        divided by.
      */}
      <DataSurface
        state={overviewResource.state}
        skeletonRows={2}
        errorMessage={describeAdminError(overviewResource.error, lang)}
        errorRetryLabel={t.retry}
        onRetry={overviewResource.retry}
      >
        {o === null ? null : (
          /*
            🔴 ONE STRIP OF SIX, NOT FOUR AND TWO. At the default four columns
            these six drew a row and a stub — "Sittings" and "Have ever played"
            floating under the left half — and between them they filled the top
            of a screen whose actual content is the enrolment bars, the games
            table and the NCF-FS coverage below. Six `xs` tiles read left to
            right in one pass and hand the page back to what it is a report of.

            ⚠️ SAME SIX MEASURES, SAME TONES, SAME CAPTIONS — including the two
            that exist to be read ("every child is in a class", "all time, not
            this window"). Only the box is smaller. This is the same change the
            teacher overview and the admin dashboard took; `StatCard`'s `xs`
            note is why a narrow column no longer wraps a label.
          */
          <StatGrid columns={6}>
            <StatCard
              label={t.reportsChildren}
              value={o.childCount}
              /* 🔴 A CHILD WITH NO CLASS IS INVISIBLE TO EVERY SCOPED QUERY IN
                 THE PRODUCT, so this is the one place anybody can see them. It
                 should be zero, and saying so is how it gets fixed. */
              caption={
                o.childrenWithNoClass > 0
                  ? t.reportsNoClass(o.childrenWithNoClass)
                  : t.reportsAllPlaced
              }
              icon={<ChildrenIcon size={16} />}
              tone="primary"
              size="xs"
            />
            <StatCard
              label={t.reportsClasses}
              value={o.classCount}
              caption={t.reportsArchived(o.archivedClassCount)}
              icon={<SchoolIcon size={16} />}
              tone="indigo"
              size="xs"
            />
            <StatCard
              label={t.reportsTeachers}
              value={o.teacherCount}
              icon={<PointIcon size={16} />}
              tone="teal"
              size="xs"
            />
            <StatCard
              label={t.reportsPlayed}
              value={`${String(o.activeThisWeek)} of ${String(o.childCount)}`}
              caption={t.reportsWindow(o.windowDays)}
              icon={<PlayIcon size={16} />}
              tone="leaf"
              size="xs"
            />
            <StatCard
              label={t.reportsSittings}
              value={o.sessionsThisWeek}
              caption={t.reportsWindow(o.windowDays)}
              icon={<ReportsIcon size={16} />}
              tone="sunshine"
              size="xs"
            />
            <StatCard
              label={t.reportsEverPlayed}
              value={`${String(o.childrenWithAnyEvents)} of ${String(o.childCount)}`}
              caption={t.reportsEverPlayedHint}
              icon={<ChildrenIcon size={16} />}
              tone="coral"
              size="xs"
            />
          </StatGrid>
        )}
      </DataSurface>

      {/*
        🔴 THE SAME FIGURES AS THE ROW ABOVE, DRAWN — and drawn from the same
        object, so the picture cannot drift from the tiles. Two bars rather than
        a split of one: "played this week" and "has ever played" overlap, and a
        stacked bar would claim they partition the roll when a child is in both.
      */}
      <DataSurface
        state={overviewResource.state}
        skeletonRows={3}
        errorMessage={describeAdminError(overviewResource.error, lang)}
        errorRetryLabel={t.retry}
        onRetry={overviewResource.retry}
      >
        {o === null ? null : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ChartPanel title={t.chartReachTitle} hint={t.chartReachHint(o.windowDays)}>
              <ShareBars
                emptyMessage={t.chartNoChildren}
                rows={[
                  {
                    key: 'played',
                    label: t.chartPlayedRow(o.windowDays),
                    count: o.activeThisWeek,
                    total: o.childCount,
                    tone: 'leaf',
                  },
                  {
                    key: 'ever',
                    label: t.chartEverRow,
                    count: o.childrenWithAnyEvents,
                    total: o.childCount,
                    tone: 'teal',
                  },
                ]}
              />
            </ChartPanel>

            {/*
              ⚠️ A DONUT ONLY BECAUSE THIS ONE GENUINELY PARTITIONS. Every child
              sits in exactly one class and every class has exactly one band, so
              the slices add to the roll. Children with no class at all are the
              exception the tile above names, and they are absent from this
              total by construction — which is why the centre is labelled "in
              classes" rather than "children".
            */}
            <ChartPanel title={t.chartBandsTitle} hint={t.chartBandsHint}>
              <Donut
                slices={bands}
                total={bands.reduce((sum, b) => sum + b.value, 0)}
                centreLabel={t.chartInClasses}
                emptyMessage={t.chartNoClasses}
              />
            </ChartPanel>
          </div>
        )}
      </DataSurface>

      {/*
        🔴 WHICH CLASS NEEDS THE HEAD TEACHER, AS A PICTURE. The table further
        down carries the same rows and the same numbers; this is the shape that
        makes a class stand out at a glance, sorted the same way so the eye and the
        table agree.
      */}
      <DataSurface
        state={overviewResource.state}
        skeletonRows={3}
        errorMessage={describeAdminError(overviewResource.error, lang)}
        errorRetryLabel={t.retry}
        onRetry={overviewResource.retry}
      >
        {o === null ? null : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ChartPanel title={t.chartClassReachTitle} hint={t.chartClassReachHint(o.windowDays)}>
              <ShareBars
                emptyMessage={t.chartNoClasses}
                rows={classes.map((c) => ({
                  key: c.classId,
                  label: c.name,
                  count: c.activeThisWeek,
                  total: c.childCount,
                  tone: c.activeThisWeek === 0 ? 'coral' : 'leaf',
                }))}
              />
            </ChartPanel>

            {/*
              ⚠️ SITTINGS ARE AN ABSOLUTE COUNT, SO COLUMNS RATHER THAN SHARES.
              There is no denominator for "how many sittings should a class have
              had" — inventing one is what a target is — so the scale is the
              busiest class and the figure sits above each column.
            */}
            <ChartPanel title={t.chartSittingsTitle} hint={t.chartSittingsHint(o.windowDays)}>
              <Columns
                emptyMessage={t.chartNoClasses}
                rows={liveClasses.map((c) => ({
                  key: c.classId,
                  label: c.name,
                  value: c.sessionsThisWeek,
                  tone: 'indigo',
                }))}
              />
            </ChartPanel>
          </div>
        )}
      </DataSurface>

      {/*
        🔴 THE PANEL THE PARENT, TEACHER AND OPERATOR TIERS ALL DRAW, over this
        school's own children. `GET /school/games` has existed since the tier was
        built and nothing called it — so the one screen a head teacher opens to
        ask "are they using it" could not say which games.

        ⚠️ THE SHARED COMPONENT, NOT A FOURTH TABLE. `architecture.md` §12: define
        each metric once, scope-filter it per tier. A hand-built table here is how
        a school total stops matching the class panels inside it.
      */}
      <DataSurface
        state={gamesResource.state}
        skeletonRows={5}
        errorMessage={describeAdminError(gamesResource.error, lang)}
        errorRetryLabel={t.retry}
        onRetry={gamesResource.retry}
      >
        {games === null ? null : (
          <GamesPanel
            games={games.games}
            summary={games.summary}
            /* 🔴 THE DENOMINATOR TRAVELS WITH THE ROWS. Without it the "children"
               column is an unreadable number — OD-39/OD-72. */
            childCount={games.childCount}
            lang={lang}
            copy={{
              ...GAMES_PANEL_COPY[lang],
              title: t.reportsGamesTitle,
              hint: t.reportsGamesHint,
            }}
          />
        )}
      </DataSurface>

      {/*
        🔴 THE GAMES PANEL'S OWN ROWS, DRAWN THREE WAYS — reach, work and
        accuracy are three different questions and a single table makes a reader
        do the comparison in their head. Every figure is a field of `GameStat`;
        nothing here is computed except which rows to show.
      */}
      <DataSurface
        state={gamesResource.state}
        skeletonRows={4}
        errorMessage={describeAdminError(gamesResource.error, lang)}
        errorRetryLabel={t.retry}
        onRetry={gamesResource.retry}
      >
        {games === null ? null : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <ChartPanel title={t.chartGameReachTitle} hint={t.chartGameReachHint}>
              <ShareBars
                emptyMessage={t.chartNoGames}
                rows={games.games.map((g) => ({
                  key: g.game,
                  label: GAME_TITLES[g.game as GameId]?.[lang] ?? g.game,
                  count: g.children,
                  total: games.childCount,
                  tone: g.children === 0 ? 'coral' : 'primary',
                }))}
              />
            </ChartPanel>

            <ChartPanel title={t.chartRoundsTitle} hint={t.chartRoundsHint}>
              <Columns
                emptyMessage={t.chartNoGames}
                rows={games.games.map((g) => ({
                  key: g.game,
                  label: GAME_TITLES[g.game as GameId]?.[lang] ?? g.game,
                  value: g.rounds,
                  tone: 'sunshine',
                }))}
              />
            </ChartPanel>

            {/*
              🔴 RIGHT AGAINST WRONG, AND THE PERCENTAGE IS THE SERVER'S.
              `accuracyPct` is folded once over the set the server scoped;
              dividing `correct` by the pair again here would be a second
              implementation of the same number, and the two would agree right
              up until one of them moved.

              ⚠️ AND IT IS NOT A SCORE FOR A CHILD. This is per game across the
              school — the distinction `design-system.md` §8 draws, and the
              reason "Average Score" was deleted from this screen rather than
              wired.
            */}
            <div className="lg:col-span-2">
              <ChartPanel title={t.chartAnswersTitle} hint={t.chartAnswersHint}>
                <SplitBars
                  emptyMessage={t.chartNoGames}
                  rows={games.games.map((g) => ({
                    key: g.game,
                    label: GAME_TITLES[g.game as GameId]?.[lang] ?? g.game,
                    first: g.correct,
                    second: g.wrong,
                    firstLabel: t.chartRight,
                    secondLabel: t.chartWrong,
                    trailing:
                      g.accuracyPct === null ? undefined : `${String(g.accuracyPct)}%`,
                  }))}
                />
              </ChartPanel>
            </div>
          </div>
        )}
      </DataSurface>

      {/*
        🔴 CURRICULUM COVERAGE — THE ONE A SCHOOL IS ASKED FOR AT AN INSPECTION,
        and the endpoint behind it (`GET /school/coverage`) had no caller at all.
        `CoverageRing` and `CodePill` in `SchoolPlayfulUI.tsx` were written for
        this panel and had never been given data either.

        ⚠️ ONE EVENT COUNTS AS EVIDENCE, WHICH IS WHY THIS IS NOT ENGAGEMENT.
        Coverage answers *"has this school evidenced this competency"*; the games
        panel above answers *"is this school using the product"*. A code covered
        once and a game played daily are different claims and must not be read as
        one, so they sit in separate panels with separate words.
      */}
      <div>
        <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-dash text-dash-md font-extrabold text-ink">
              {t.reportsCoverageTitle}
            </h2>
            <p className="mt-0.5 font-dash text-dash-xs text-muted">{t.reportsCoverageHint}</p>
          </div>
          {/* 🔴 A REAL FILE FROM THE ROWS ON SCREEN. This button ran
              `alert('Exporting full analytics report...')`. */}
          <button
            type="button"
            onClick={exportCoverage}
            disabled={coverage === null || coverage.rows.length === 0}
            className="rounded-xl border border-line bg-surface px-3.5 py-2 font-dash text-dash-xs font-bold text-ink shadow-2xs transition-colors hover:bg-canvas disabled:opacity-40"
          >
            {t.reportsExportCoverage}
          </button>
        </div>

        <DataSurface
          state={coverageResource.state}
          skeletonRows={4}
          errorMessage={describeAdminError(coverageResource.error, lang)}
          errorRetryLabel={t.retry}
          onRetry={coverageResource.retry}
        >
          {coverage === null ? null : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="rounded-lg border border-line bg-surface p-4 shadow-card sm:p-6">
                <CoverageRing
                  covered={coverage.codesCovered.length}
                  total={coverage.codesCovered.length + coverage.codesUncovered.length}
                />
              </div>

              <div className="lg:col-span-2 flex flex-col gap-6">
                {/*
                  ⚠️ CODES PER GAME IS NOT COVERAGE OF THE CURRICULUM. Each row
                  carries the codes that game can evidence, and the ring beside
                  it carries how many of the school's codes are evidenced at all.
                  Charting the first as a share of the second would divide two
                  different denominators — so this one is drawn as counts.
                */}
                <ChartPanel title={t.chartEvidenceTitle} hint={t.chartEvidenceHint}>
                  <Columns
                    emptyMessage={t.reportsCoverageEmpty}
                    rows={coverage.rows.map((row) => ({
                      key: row.game,
                      label: GAME_TITLES[row.game as GameId]?.[lang] ?? row.game,
                      value: row.events,
                      tone: row.events === 0 ? 'coral' : 'leaf',
                    }))}
                  />
                </ChartPanel>

                {coverage.rows.length === 0 ? (
                  <p className="rounded-lg border border-line bg-surface p-6 text-center font-dash text-dash-xs font-semibold text-muted shadow-card">
                    {t.reportsCoverageEmpty}
                  </p>
                ) : (
                  <TableCard>
                    <table className="w-full border-collapse">
                      <Thead
                        headers={[
                          t.reportsGame,
                          t.reportsCategory,
                          t.reportsCodes,
                          t.reportsEvents,
                          t.reportsChildrenCol,
                        ]}
                      />
                      <tbody>
                        {coverage.rows.map((row) => (
                          <Tr key={row.game}>
                            <Td bold>{GAME_TITLES[row.game as GameId]?.[lang] ?? row.game}</Td>
                            <Td soft>{row.category}</Td>
                            <Td soft>{row.codes.length}</Td>
                            <Td num>{row.events}</Td>
                            <Td num>{row.children}</Td>
                          </Tr>
                        ))}
                      </tbody>
                    </table>
                  </TableCard>
                )}

                {/*
                  🔴 THE CODES NOBODY HAS EVIDENCED, NAMED. A count of gaps
                  leaves a head teacher with nothing to plan around; the codes
                  themselves are what goes on next term's timetable.
                */}
                {coverage.codesUncovered.length > 0 && (
                  <div className="mt-4">
                    <p className="mb-2 font-dash text-dash-xs font-bold text-muted">
                      {t.reportsGaps(coverage.codesUncovered.length)}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {coverage.codesUncovered.map((code) => (
                        <CodePill key={code} code={code} covered={false} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DataSurface>
      </div>

      {/*
        🔴 WHICH CLASS NEEDS THE HEAD TEACHER, WORST FIRST. The screen this
        replaces ranked five invented classes by an invented score out of a
        hundred — and a ranked list by score is the one shape this product
        refuses to publish about children. This ranks by how many of a class's
        own children played, which is a fact about the adults' rollout rather
        than a grade about the children.
      */}
      <DataSurface
        state={overviewResource.state}
        skeletonRows={4}
        errorMessage={describeAdminError(overviewResource.error, lang)}
        errorRetryLabel={t.retry}
        onRetry={overviewResource.retry}
      >
        {o === null ? null : (
          <div>
            <h2 className="font-dash text-dash-md font-extrabold text-ink">
              {t.reportsClassesTitle}
            </h2>
            <p className="mt-0.5 mb-3 font-dash text-dash-xs text-muted">
              {t.reportsClassesHint(o.windowDays)}
            </p>
            {classes.length === 0 ? (
              <p className="rounded-lg border border-line bg-surface p-6 text-center font-dash text-dash-xs font-semibold text-muted shadow-card">
                {t.reportsClassesEmpty}
              </p>
            ) : (
              <TableCard>
                <table className="w-full border-collapse">
                  <Thead
                    headers={[
                      t.reportsClassCol,
                      t.reportsTeacherCol,
                      t.reportsChildrenCol,
                      t.reportsPlayedCol,
                      t.reportsSittingsCol,
                    ]}
                  />
                  <tbody>
                    {classes.map((c) => (
                      <Tr key={c.classId}>
                        <Td bold>{c.name}</Td>
                        {/* 🔴 A CLASS WITH NO TEACHER IS THE FINDING, not a blank.
                            It is why a class can sit at zero all term. */}
                        <Td soft>{c.teacherName ?? t.reportsNoTeacher}</Td>
                        <Td num>{c.childCount}</Td>
                        <Td num>{`${String(c.activeThisWeek)} of ${String(c.childCount)}`}</Td>
                        <Td num>{c.sessionsThisWeek}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </table>
              </TableCard>
            )}
          </div>
        )}
      </DataSurface>

      {/*
        ⚠️ SKILLS ACROSS THE SCHOOL, AS COUNTS. `SchoolSkillRow` carries events
        and the children behind them, deliberately without a ratio — the fold's
        own note explains that a percentage here would be a grade. The pair is
        what a head teacher can act on.
      */}
      <DataSurface
        state={overviewResource.state}
        skeletonRows={4}
        errorMessage={describeAdminError(overviewResource.error, lang)}
        errorRetryLabel={t.retry}
        onRetry={overviewResource.retry}
      >
        {o === null ? null : o.skills.length === 0 ? null : (
          <div>
            <h2 className="font-dash text-dash-md font-extrabold text-ink">
              {t.reportsSkillsTitle}
            </h2>
            <p className="mt-0.5 mb-3 font-dash text-dash-xs text-muted">{t.reportsSkillsHint}</p>
            {/*
              ⚠️ TWO CHARTS OF ONE FOLD, BECAUSE THE TWO COLUMNS ANSWER OPPOSITE
              QUESTIONS. "How many children have touched this" is a share of the
              roll; "how much has been recorded against it" is an absolute count
              and has no denominator at all. One chart with both would be two
              scales on one axis.
            */}
            <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <ChartPanel title={t.chartSkillReachTitle} hint={t.chartSkillReachHint}>
                <ShareBars
                  emptyMessage={t.chartNoSkills}
                  rows={o.skills.map((row) => ({
                    key: row.skill,
                    label: SKILL_LABEL[row.skill]?.[lang] ?? row.skill,
                    count: row.children,
                    total: o.childCount,
                    tone: 'teal',
                  }))}
                />
              </ChartPanel>
              <ChartPanel title={t.chartSkillEventsTitle} hint={t.chartSkillEventsHint}>
                <Columns
                  emptyMessage={t.chartNoSkills}
                  rows={o.skills.map((row) => ({
                    key: row.skill,
                    label: SKILL_LABEL[row.skill]?.[lang] ?? row.skill,
                    value: row.events,
                    tone: 'primary',
                  }))}
                />
              </ChartPanel>
            </div>

            <TableCard>
              <table className="w-full border-collapse">
                <Thead
                  headers={[t.reportsSkillCol, t.reportsEvents, t.reportsChildrenCol]}
                />
                <tbody>
                  {o.skills.map((row) => (
                    <Tr key={row.skill}>
                      {/* 🔴 THE LABEL, NOT THE ENUM. This printed NUMERACY and
                          GENERAL_AWARENESS — the database's words — at a head
                          teacher, which is the complaint `gameArt.ts` and the
                          parent tier's sticker shelf both record. `SKILL_LABEL`
                          is the tier's own table and was already imported by the
                          student report next door. */}
                      <Td bold>{SKILL_LABEL[row.skill]?.[lang] ?? row.skill}</Td>
                      <Td num>{row.events}</Td>
                      {/* 🔴 THE DENOMINATOR IS THE SCHOOL'S OWN ROLL. */}
                      <Td num>{`${String(row.children)} of ${String(o.childCount)}`}</Td>
                    </Tr>
                  ))}
                </tbody>
              </table>
            </TableCard>
          </div>
        )}
      </DataSurface>
    </section>
  )
}
